import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";
import allLocales from "@fullcalendar/core/locales-all";
import {
  CaciStyles, PageHeader, Seal,
  useScrollProgress, langPath, formatDate,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   CALENDRIER
   ───────────────────────────────────────────────────────────────────────────
   Same FullCalendar setup, same /api/events source, same views. Changes:

   · The event popup was a bare div with no backdrop, no z-index and no
     positioning of its own — it relied on a `.event-modal` class the theme
     defines elsewhere, so it rendered inline in the page flow underneath the
     calendar. It is now a real dialog: backdrop, Escape to close, focus
     moved into it, scroll locked behind it.
   · "Voir les détails" linked to `/${slug}` — a bare slug at the site root,
     which matches no route. It now points at the event's real address.
   · The link opened in a new tab for an internal page, which is not something
     an in-site link should do.
   · Dates in the popup were printed raw from the API. They now follow the
     interface language.
   · The breadcrumb linked to a hard-coded "/", dropping Arabic and English
     visitors onto the French homepage.
   · A failed fetch only reached the console: the calendar simply showed an
     empty month, which reads as "no events" rather than "something broke".
   · dayMaxEvents was false and the "+N more" link hidden, so a busy day grew
     the row without limit and pushed the rest of the month off-screen. Days
     now cap at four with a working "+N more".
   ═══════════════════════════════════════════════════════════════════════════ */

function Calendrier() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [selected, setSelected] = useState(null);
  const [failed, setFailed] = useState(false);
  const calRef = useRef(null);
  const dialogRef = useRef(null);
  const lastFocus = useRef(null);
  const progress = useScrollProgress();

  const locale = lang === "ar" ? "ar-dz" : lang;

  const fetchEvents = useCallback(async (info, ok, fail) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/events`, {
        params: { start: info.start.toISOString(), end: info.end.toISOString() },
        headers: { "Accept-Language": lang },
      });
      setFailed(false);
      ok(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Calendrier: could not load events —", err);
      setFailed(true);
      fail(err);
    }
  }, [lang]);

  const onEventClick = (info) => {
    info.jsEvent.preventDefault();
    lastFocus.current = document.activeElement;
    const p = info.event.extendedProps || {};
    setSelected({
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      starts_at: p.event_starts_at,
      ends_at: p.event_ends_at,
      image: p.image,
      slug: p.slug,
      lieu: p.lieu,
    });
  };

  const close = useCallback(() => {
    setSelected(null);
    /* Send the caret back to the day cell that opened the dialog, so keyboard
       users are not dropped at the top of the document. */
    const el = lastFocus.current;
    if (el && typeof el.focus === "function") el.focus();
  }, []);

  useEffect(() => {
    if (!selected) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const timer = window.requestAnimationFrame(() => {
      dialogRef.current && dialogRef.current.focus();
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      window.cancelAnimationFrame(timer);
    };
  }, [selected, close]);

  /* The old link was `/${slug}` — a bare slug at the site root, which matches
     no route in the app, so "Voir les détails" was a guaranteed 404. The
     router declares the event page as /event/:slug/register (and its
     language-prefixed twin); with no slug we fall back to the events list
     rather than inventing an address. */
  const eventHref = (ev) => {
    if (ev && ev.slug) {
      return langPath(lang, `/event/${encodeURIComponent(ev.slug)}/register`);
    }
    return langPath(lang, "/evenements");
  };

  const when = (raw, date) => {
    if (raw) {
      const parsed = formatDate(raw, lang, "");
      if (parsed) return parsed;
      return raw;
    }
    return date ? formatDate(date.toISOString(), lang, "") : "";
  };

  const pageTitle = t("menu.calendrier");

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.calendrier_sous_title", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("home.calendrier_sous_title", pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        sub={t("home.calendrier_sous_title", "")}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: pageTitle }]}
      />

      <section className="caci-section">
        <div className="caci-shell">
          {failed && (
            <div className="cal-alert" role="status">
              <i className="bi bi-exclamation-triangle" />
              <span>{t("home.section_error", "Le calendrier n'a pas pu être chargé.")}</span>
              <button
                type="button" className="btn-quiet"
                onClick={() => calRef.current && calRef.current.getApi().refetchEvents()}
              >
                <i className="bi bi-arrow-clockwise" />
                {t("home.retry", "Réessayer")}
              </button>
            </div>
          )}

          <div className="cal-shell">
            <FullCalendar
              ref={calRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, multiMonthPlugin]}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,listYear",
              }}
              initialView="dayGridMonth"
              editable={false}
              selectable={false}
              events={fetchEvents}
              locale={locale}
              locales={allLocales}
              direction={isRtl ? "rtl" : "ltr"}
              eventClick={onEventClick}
              height="auto"
              handleWindowResize
              stickyHeaderDates={false}
              /* Four per day with a real "+N more". Uncapped rows meant one
                 busy day could push the rest of the month off the screen. */
              dayMaxEvents={4}
              eventDisplay="block"
              buttonText={{
                today: t("calendar.today", "Aujourd'hui"),
                month: t("calendar.month", "Mois"),
                week: t("calendar.week", "Semaine"),
                list: t("calendar.list", "Liste"),
              }}
              noEventsText={t("calendar.none", "Aucun événement sur cette période")}
            />
          </div>
        </div>
      </section>

      {/* ══════════════════ EVENT DIALOG ══════════════════ */}
      {selected && (
        <div className="cal-backdrop" onClick={close}>
          <div
            className="cal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cal-dialog-title"
            tabIndex={-1}
            ref={dialogRef}
            dir={isRtl ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-grip" />
            <button
              type="button" className="cal-close"
              onClick={close} aria-label={t("home.Close", "Fermer")}
            >
              <i className="bi bi-x-lg" />
            </button>

            {selected.image && (
              <div className="cal-dialog-img">
                <img
                  src={`${BASE_URL}${selected.image}`}
                  alt="" loading="lazy"
                  onError={(e) => { e.currentTarget.closest(".cal-dialog-img").style.display = "none"; }}
                />
              </div>
            )}

            <div className="cal-dialog-body">
              <span className="cal-eyebrow">
                <Seal size={13} />
                {t("menu.calendrier")}
              </span>
              <h2 id="cal-dialog-title" dir="auto">{selected.title}</h2>

              <dl className="cal-facts">
                <div>
                  <dt><i className="bi bi-calendar-check" />{t("home.start_date")}</dt>
                  <dd>{when(selected.starts_at, selected.start)}</dd>
                </div>
                {(selected.ends_at || selected.end) && (
                  <div>
                    <dt><i className="bi bi-calendar-x" />{t("home.end_date")}</dt>
                    <dd>{when(selected.ends_at, selected.end)}</dd>
                  </div>
                )}
                {selected.lieu && (
                  <div>
                    <dt><i className="bi bi-geo-alt" />{t("home.Address_Us", "Lieu")}</dt>
                    <dd dir="auto">{selected.lieu}</dd>
                  </div>
                )}
              </dl>

              <div className="cal-dialog-actions">
                <button type="button" className="btn-quiet" onClick={close}>
                  {t("home.Close", "Fermer")}
                </button>
                {/* An internal page: a Link, in this tab. */}
                <Link to={eventHref(selected)} className="btn-solid" onClick={close}>
                  {t("home.view_details", "Voir les détails")}
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PAGE_CSS = `
.caci-page .cal-alert {
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
  padding: 16px 20px; margin-block-end: 20px; border-radius: 16px;
  background: rgba(163,58,43,.06); color: var(--danger);
  box-shadow: inset 0 0 0 1px rgba(163,58,43,.2);
  font-size: 14.5px;
}
.caci-page .cal-alert > span { flex: 1; min-width: 180px; }
.caci-page .cal-alert .btn-quiet { color: var(--danger); border-color: rgba(163,58,43,.35); }
.caci-page .cal-alert .btn-quiet:hover { background: var(--danger); border-color: var(--danger); color: #fff; }

/* ── FullCalendar, dressed in the site's own palette ────────────────────── */
.caci-page .cal-shell {
  border: 1px solid var(--rule); border-radius: 20px;
  background: var(--surface); padding: clamp(12px, 2.4vw, 24px);
  box-shadow: 0 20px 50px -34px rgba(8,21,14,.4);
  --fc-border-color: var(--rule);
  --fc-page-bg-color: var(--surface);
  --fc-neutral-bg-color: var(--paper);
  --fc-today-bg-color: rgba(216,180,85,.11);
  --fc-event-bg-color: var(--pine);
  --fc-event-border-color: var(--pine);
  --fc-event-text-color: #fff;
  --fc-list-event-hover-bg-color: var(--mint);
}
.caci-page .fc { font-family: var(--body); }
.caci-page .fc .fc-toolbar-title {
  font-family: var(--display); font-size: clamp(1.05rem, 3.2vw, 1.5rem);
  font-weight: 700; letter-spacing: -.024em; color: var(--ink);
}
.caci-page .fc .fc-toolbar.fc-header-toolbar {
  flex-wrap: wrap; gap: 10px; margin-block-end: clamp(14px, 2.4vw, 22px);
}
.caci-page .fc .fc-button {
  min-height: 42px; padding: 0 15px; border-radius: 999px;
  border: 1px solid var(--rule); background: var(--paper); color: var(--pine);
  font-family: var(--mono); font-size: 11.5px; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase; box-shadow: none;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e);
}
.caci-page .fc .fc-button:hover { background: var(--mint); border-color: var(--rule-2); color: var(--pine); }
.caci-page .fc .fc-button:disabled { opacity: .4; }
.caci-page .fc .fc-button-primary:not(:disabled).fc-button-active,
.caci-page .fc .fc-button-primary:not(:disabled):active {
  background: var(--pine); border-color: var(--pine); color: #fff;
}
.caci-page .fc .fc-button:focus-visible { outline: 3px solid var(--brass); outline-offset: 2px; box-shadow: none; }
.caci-page .fc .fc-button-group > .fc-button { border-radius: 999px; }
.caci-page .fc .fc-button-group > .fc-button:not(:last-child) { margin-inline-end: 6px; }

.caci-page .fc .fc-col-header-cell {
  background: var(--paper); padding-block: 10px;
}
.caci-page .fc .fc-col-header-cell-cushion {
  font-family: var(--mono); font-size: 10.5px; font-weight: 700;
  letter-spacing: .12em; text-transform: uppercase; color: var(--muted);
  text-decoration: none;
}
.caci-page .fc .fc-daygrid-day-number {
  font-family: var(--mono); font-size: 12.5px; color: var(--txt);
  text-decoration: none; padding: 7px 9px;
}
.caci-page .fc .fc-day-today .fc-daygrid-day-number { color: var(--brass); font-weight: 700; }
.caci-page .fc .fc-daygrid-day-frame { min-height: 96px; }
.caci-page .fc .fc-event {
  border-radius: 9px; border: 0; padding: 3px 7px; cursor: pointer;
  background: linear-gradient(180deg, var(--leaf), var(--pine));
  font-size: 12px; line-height: 1.35;
  transition: transform var(--t1) var(--e), box-shadow var(--t1) var(--e);
}
.caci-page .fc .fc-event:hover {
  transform: translateY(-1px); box-shadow: 0 8px 18px -8px rgba(15,74,48,.8);
}
.caci-page .fc .fc-event-title { white-space: normal; font-weight: 600; }
.caci-page .fc .fc-daygrid-more-link {
  font-family: var(--mono); font-size: 10.5px; font-weight: 700;
  letter-spacing: .06em; color: var(--brass); padding: 2px 6px;
}
.caci-page .fc .fc-list-event-title a { color: var(--ink); text-decoration: none; font-weight: 600; }
.caci-page .fc .fc-list-day-cushion { background: var(--mint); }
.caci-page .fc-theme-standard td, .caci-page .fc-theme-standard th { border-color: var(--rule); }
@media (max-width: 575.98px) {
  .caci-page .fc .fc-toolbar.fc-header-toolbar { flex-direction: column; align-items: stretch; }
  .caci-page .fc .fc-toolbar-chunk { display: flex; justify-content: center; flex-wrap: wrap; gap: 6px; }
  .caci-page .fc .fc-daygrid-day-frame { min-height: 74px; }
  .caci-page .fc .fc-event { font-size: 11px; }
}

/* ── Dialog ─────────────────────────────────────────────────────────────
   A real dialog. The old markup was an unpositioned div that fell into the
   page flow beneath the calendar. */
.cal-backdrop {
  position: fixed; inset: 0; z-index: 1100;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(5,15,10,.6);
  -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  animation: calFade .3s ease both;
}
@keyframes calFade { from { opacity: 0; } to { opacity: 1; } }
.cal-dialog {
  position: relative; width: 100%; max-width: 560px;
  max-height: 92vh; max-height: 92svh; overflow: auto; outline: none;
  background: #fff; border-radius: 22px 22px 0 0;
  box-shadow: 0 -20px 70px rgba(5,15,10,.45);
  animation: calUp .45s cubic-bezier(.22,1,.36,1) both;
  font-family: 'Instrument Sans', system-ui, sans-serif;
}
@keyframes calUp { from { transform: translateY(100%); } to { transform: none; } }
.cal-grip {
  width: 42px; height: 4px; border-radius: 4px;
  background: rgba(15,74,48,.2); margin: 10px auto 0;
}
.cal-close {
  position: absolute; z-index: 3; inset-block-start: 12px; inset-inline-end: 12px;
  width: 42px; height: 42px; border-radius: 50%; cursor: pointer;
  border: 1px solid rgba(15,74,48,.14); background: rgba(255,255,255,.92);
  color: #5C7268; display: grid; place-items: center; font-size: 15px;
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  transition: background .25s ease, color .25s ease, transform .25s ease;
}
.cal-close:hover { background: #0F4A30; color: #fff; transform: rotate(90deg); }
.cal-dialog-img { position: relative; aspect-ratio: 16 / 9; overflow: hidden; background: #E9F3ED; }
.cal-dialog-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cal-dialog-body { padding: 20px 22px 24px; }
.cal-eyebrow {
  display: inline-flex; align-items: center; gap: 8px; margin-block-end: 10px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10.5px; font-weight: 700; letter-spacing: .14em;
  text-transform: uppercase; color: #B08A2E;
}
.cal-dialog-body h2 {
  margin: 0 0 18px; font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: clamp(1.15rem, 4vw, 1.5rem); font-weight: 700;
  letter-spacing: -.024em; line-height: 1.22; color: #08150E; text-wrap: balance;
}
.cal-facts { margin: 0; display: grid; gap: 2px; }
.cal-facts > div { display: grid; gap: 3px; padding-block: 12px; }
.cal-facts > div + div { border-block-start: 1px solid rgba(15,74,48,.12); }
.cal-facts dt {
  display: flex; align-items: center; gap: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: #5C7268;
}
.cal-facts dt i { color: #B08A2E; font-size: 12px; }
.cal-facts dd { margin: 0; font-size: 15px; line-height: 1.5; color: #0B1F16; }
.cal-dialog-actions {
  display: grid; gap: 10px; margin-block-start: 22px;
}
.cal-dialog-actions .btn-quiet,
.cal-dialog-actions .btn-solid {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 50px; padding: 0 24px; border-radius: 999px;
  text-decoration: none; cursor: pointer; font-size: 15px; font-weight: 600;
  font-family: inherit;
}
.cal-dialog-actions .btn-quiet {
  background: transparent; border: 1px solid rgba(15,74,48,.22); color: #5C7268;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px; letter-spacing: .1em; text-transform: uppercase;
}
.cal-dialog-actions .btn-quiet:hover { background: #E9F3ED; color: #0F4A30; }
.cal-dialog-actions .btn-solid {
  background: linear-gradient(180deg, #22A060, #1A7A4A); color: #fff; border: 0;
  box-shadow: 0 14px 30px -14px rgba(26,122,74,.9);
}
.cal-dialog-actions .btn-solid:hover { color: #fff; transform: translateY(-2px); }
@media (min-width: 640px) {
  .cal-backdrop { align-items: center; padding: 24px; }
  .cal-dialog { border-radius: 24px; animation-name: calIn; }
  .cal-grip { display: none; }
  @keyframes calIn {
    from { opacity: 0; transform: translateY(22px) scale(.97); }
    to   { opacity: 1; transform: none; }
  }
  .cal-dialog-actions { grid-auto-flow: column; justify-content: flex-end; }
}
@media (prefers-reduced-motion: reduce) {
  .cal-backdrop, .cal-dialog { animation: none !important; }
}
`;

export default Calendrier;
